import { useCallback, useMemo, useState } from 'react';
import { ProductType } from '@/data/types/Product';
import { ProductLiteModern } from '@/components/blocks/ProductLiteModern';
import { CLICK_PRODUCT_VIEW_SOURCE } from '@/data/constants/clickProductView';
import { DpModernProductCardVariant } from '@/components/blocks/DpModernProductCard';
import { Skeleton } from '@mui/material';
import { FeaturedProductCardWrapper } from '@/components/content/CatalogEntryIARecommendation/parts/FeaturedProductCardWrapper';

const SKELETON_SIZE_BY_VARIANT: Record<DpModernProductCardVariant, { width: any; height: any }> = {
	mini: { width: 130, height: 235 },
	medium: { width: 190, height: 325 },
	large: {
		width: { xs: 180, sm: 290 },
		height: { xs: 350, sm: 460 },
	},
	largeRepurchase: {
		width: { xs: 180, sm: 290 },
		height: { xs: 375, sm: 490 },
	},
};

export const cardSkeletonContainerMapper = (
	partNumbers: string[],
	variant: DpModernProductCardVariant = 'large'
) => {
	const { width, height } = SKELETON_SIZE_BY_VARIANT[variant];
	return partNumbers.map((partNumber) => ({
		partNumber,
		card: <Skeleton variant="rectangular" sx={{ width, height }} />,
		a11y: { 'aria-label': undefined, 'aria-labelledby': partNumber },
	}));
};

interface InfoElectronico {
	offerDeContado?: string;
	strikedDeContado?: string;
}

interface ProductTypeWithInfoElectronico extends ProductType {
	infoElectronico?: InfoElectronico;
}

type ListTrackingContext = {
	parentPartNumber?: string;
};

export const cardContainerMapper = ({
	products,
	contract,
	tracking,
	variant,
	clickSource,
	onClick,
}: {
	products: ProductTypeWithInfoElectronico[];
	contract: string;
	tracking: ListTrackingContext;
	variant?: DpModernProductCardVariant;
	clickSource?: CLICK_PRODUCT_VIEW_SOURCE;
	onClick?: (partNumber: string) => void;
}) =>
	products.map((product, index) => ({
		partNumber: product.partNumber,
		card: (
			<ProductLiteModern
				key={`${contract}_${product.partNumber}_${index}`}
				product={product}
				contract={contract}
				variant={variant}
				clickSource={clickSource}
				clickAction={onClick?.bind(null, product.partNumber)}
				index={index + 1}
				parentPartNumber={tracking.parentPartNumber}
			/>
		),
		a11y: { 'aria-label': undefined, 'aria-labelledby': product.partNumber },
	}));

export const useCatalogEntryIARecommendationItems = ({
	products,
	isLoadingProducts,
	parentPartNumber,
	variant,
	clickSource,
	contract,
	params,
	transforms,
	onClick,
}: {
	isLoadingProducts: boolean;
	products: ProductType[];
	parentPartNumber?: string;
	variant?: DpModernProductCardVariant;
	clickSource?: CLICK_PRODUCT_VIEW_SOURCE;
	contract: string;
	params: {
		partNumber?: string;
	};
	transforms?: {
		includePartNumber?: boolean;
		highlightedPartNumber?: string;
	};
	onClick?: (partNumber: string) => void;
}) => {
	const [invalid, setInvalid] = useState<Record<string, boolean>>({});

	const filteredProducts = useMemo(() => {
		if (!products?.length || !params?.partNumber) {
			return products;
		}

		if (transforms?.includePartNumber) {
			const idx = products.findIndex((p) => p.partNumber === params.partNumber);
			if (idx <= 0) return products;
			const sorted = [...products];
			sorted.unshift(sorted.splice(idx, 1)[0]);
			return sorted;
		}

		return products.filter((p) => p.partNumber !== params.partNumber);
	}, [products, params?.partNumber, transforms?.includePartNumber]);

	const originalSliders = useMemo(() => {
		if (isLoadingProducts) {
			return cardSkeletonContainerMapper(new Array(4).fill(''), variant);
		}

		return cardContainerMapper({
			products: filteredProducts,
			contract,
			clickSource,
			tracking: { parentPartNumber },
			variant,
			onClick,
		});
	}, [isLoadingProducts, filteredProducts, clickSource, parentPartNumber, variant, onClick]);

	const { slides } = useMemo(() => {
		const filtered = originalSliders.filter(
			({ partNumber }) => !invalid[`${partNumber}_${contract}`]
		);
		const slides = filtered.map(({ card, partNumber }) => {
			const shouldHighlight = partNumber === transforms?.highlightedPartNumber;

			return shouldHighlight ? (
				<FeaturedProductCardWrapper key={`featured_${partNumber}`}>
					{card}
				</FeaturedProductCardWrapper>
			) : (
				card
			);
		});
		const a11yProps = filtered.map(({ a11y }) => a11y);

		return { slides, a11yProps };
	}, [invalid, originalSliders, transforms]);

	const onNotify = useCallback(
		(id: string, usedContract: string, product: ProductType) =>
			setInvalid((old) => ({ ...old, [`${id}_${usedContract}`]: !product })),
		[]
	);

	return {
		slides,
		onNotify,
	};
};
